import { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import {
  Box,
  Grid,
  GridProps,
  Heading,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Form } from 'react-final-form';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useInsertQuestionAndAnswerMutation } from '@/generated/graphql';
import useValidationSchema from '@/lib/hooks/useValidationSchema';
import { ListingProps } from '@/types/listing';
import ContactInfoStack, {
  getContactInfoStackItems,
} from '@/components/common/ContactInfoStack';
import { getLocalizedRegionNames } from '@/lib/regions';
import { onAnalyticsFormEvent } from '@/lib/utils/analytics';
import ContactForm from './Form';
import Schema, { SchemaType } from './schema';
import UserAvatar from '../../common/UserAvatar';
import ButtonLink from '../ButtonLink';
import { urls } from '@/lib/urls';

const analyticsFormName = 'Yhteydenotto välittäjälle kohteesta';

const ContactBlock = ({
  listing,
  ...gridStyles
}: Pick<ListingProps, 'listing'> & GridProps) => {
  const { t } = useTranslation('frontoffice');
  const { locale } = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [insert] = useInsertQuestionAndAnswerMutation();
  const toast = useToast();
  const { executeRecaptcha, loaded } = useReCaptcha();

  const onSubmit = async (values: SchemaType) => {
    try {
      let token = '';
      if (loaded) {
        token = await executeRecaptcha('insert_listing_question');
      }
      await insert({
        variables: {
          ...values,
        },
        context: {
          headers: {
            'x-captcha-token': token,
          },
        },
      });
      onAnalyticsFormEvent('submission_success', { name: analyticsFormName });
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      onAnalyticsFormEvent('submission_failed', { name: analyticsFormName });
      toast({
        title: t('toast.error', 'Virhe'),
        description: t(
          'toast.error_message',
          'Tapahtui virhe, yritä uudelleen',
        ),
        status: 'error',
        duration: 4000,
        position: 'top',
        isClosable: true,
      });
    }
  };

  const initialValues: SchemaType = {
    listing_id: listing.id || 0,
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    sender_message: '',
    marketing_consent: false,
    publish_consent: false,
    language: locale || 'fi',
  };

  const validate = useValidationSchema(Schema);

  if (typeof listing.id === 'undefined' && !submitted) {
    return null;
  }

  return (
    <Grid
      templateColumns="repeat(auto-fill, minmax(260px, 1fr))"
      gap={[2, null, 6]}
      {...gridStyles}
    >
      <Box sx={{ '@media print': { display: 'none' } }}>
        {!submitted ? (
          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={(props) => <ContactForm {...props} />}
          />
        ) : (
          <Heading>
            {t('thank_you_for_contacting', 'Kiitos yhteydenotostanne!')}
          </Heading>
        )}
      </Box>
      <>
        {listing?.sales_assignment?.sales_assignment_users?.map(
          ({
            user: {
              id: realtor_id,
              email,
              avatar_attachment,
              name,
              title,
              working_area,
              phone_number,
              contact_email,
              real_estate_agency,
            },
          }) => (
            <Box key={email} className="contact-person">
              <UserAvatar size="xl" mb={5} user={{ name, avatar_attachment }} />
              <Heading as="h4" size="md">
                {name}
              </Heading>
              <Text mb={5}>{title}</Text>
              <ContactInfoStack
                items={getContactInfoStackItems({
                  email: contact_email || email,
                  phone: phone_number,
                  areas: getLocalizedRegionNames(
                    working_area as string[],
                    locale,
                  ).join(', '),
                  company: real_estate_agency?.name,
                })}
                align="flex-start"
                displayLinks
                mb={4}
              />
              <ButtonLink
                link={{
                  url: urls.public.realtors.get({
                    id: realtor_id,
                    name: name || 'valittaja',
                  }),
                  title: t('get_to_know', 'Tutustu'),
                  altTitle: `${t('get_to_know', 'Tutustu')} ${name}`,
                  pt: '4',
                }}
                colorScheme="primary"
                variant="solid"
                mb="0"
              />
            </Box>
          ),
        )}
      </>
    </Grid>
  );
};

export default ContactBlock;
